import React, { FormEvent, useState } from 'react';

import { AttachFile } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Link,
  List,
  ListItem,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';
import Accordion from '~/components/v2/common/Accordion/Accordion';

import {
  useDeleteOpportunityDocument,
  useDownloadOpportunityDocument,
  useGetOpportunityDocuments,
  useUploadOpportunityDocument,
} from '~/hooks/apis/offers/useOpportunityDocuments';

import { type OpportunitySnapshotDocument } from '~/services/OffersService';

import { validateDocumentFile } from '~/utils/opportunityDocumentFile';

interface Props {
  opportunityId: string;
}

export default function OpportunityDocuments({ opportunityId }: Props) {
  const [name, setName] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);

  const showConfirmDialog = useConfirmDialog();
  const documentsQuery = useGetOpportunityDocuments(opportunityId);
  const uploadMutation = useUploadOpportunityDocument(opportunityId);
  const downloadMutation = useDownloadOpportunityDocument(opportunityId);
  const deleteMutation = useDeleteOpportunityDocument(opportunityId);

  const documents = documentsQuery.data ?? [];
  const isLoading = documentsQuery.isLoading;
  const isSubmitting = uploadMutation.isPending;
  const displayError =
    error ||
    (documentsQuery.error instanceof Error ? documentsQuery.error.message : null) ||
    (uploadMutation.error instanceof Error ? uploadMutation.error.message : null) ||
    (downloadMutation.error instanceof Error ? downloadMutation.error.message : null) ||
    (deleteMutation.error instanceof Error ? deleteMutation.error.message : null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextFile = event.target.files?.[0] ?? null;
    event.target.value = '';
    setFile(nextFile);
    setError(nextFile ? validateDocumentFile(nextFile) : null);
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!name.trim() || !file) {
      setError('Name of doc and file are required');
      return;
    }
    const validationError = validateDocumentFile(file);
    if (validationError) {
      setError(validationError);
      return;
    }
    setError(null);
    uploadMutation.mutate(
      { uploadName: name.trim(), uploadFile: file },
      {
        onSuccess: () => {
          setName('');
          setFile(null);
          setError(null);
        },
      },
    );
  };

  const handleDownload = (document: OpportunitySnapshotDocument) => {
    setError(null);
    downloadMutation.mutate(document);
  };

  const handleDelete = async (document: OpportunitySnapshotDocument) => {
    const confirmed = await showConfirmDialog({
      title: 'Delete document',
      description: `Delete “${document.name}”? This cannot be undone.`,
    });
    if (!confirmed) {
      return;
    }
    setError(null);
    deleteMutation.mutate(document);
  };

  return (
    <Box id="documents" mt={2}>
      <Accordion title="Documents" icon={<AttachFile />} columns={1}>
        <Stack spacing={2} component="form" onSubmit={handleSubmit}>
          {displayError && <Alert severity="error">{displayError}</Alert>}
          <TextField
            required
            label="Name of doc"
            value={name}
            onChange={(event) => setName(event.target.value)}
            disabled={isSubmitting}
          />
          <Button variant="outlined" component="label" disabled={isSubmitting}>
            {file ? file.name : 'Choose file'}
            <input hidden type="file" onChange={handleFileChange} />
          </Button>
          <Box>
            <Button type="submit" variant="contained" disabled={isSubmitting || !name.trim() || !file}>
              {isSubmitting ? 'Uploading…' : 'Upload'}
            </Button>
          </Box>
          {isLoading ? (
            <CircularProgress size={24} />
          ) : documents.length === 0 ? (
            <Typography variant="body2">No documents uploaded yet.</Typography>
          ) : (
            <List dense>
              {documents.map((document) => (
                <ListItem
                  key={document.id}
                  secondaryAction={
                    <Button
                      type="button"
                      color="error"
                      size="small"
                      disabled={deleteMutation.isPending}
                      onClick={() => handleDelete(document)}
                    >
                      Delete
                    </Button>
                  }
                >
                  <ListItemText
                    primary={
                      <Link component="button" type="button" onClick={() => handleDownload(document)}>
                        {document.name}
                      </Link>
                    }
                    secondary={document.filename}
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Stack>
      </Accordion>
    </Box>
  );
}
